import { request } from '@umijs/max';
import { API } from './typings';

/** 登录接口 POST /api/login/account */
export async function login(body: API.LoginParams, options?: { [key: string]: any }) {
  return request<API.LoginResult>('/api/login/account', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 退出登录接口 POST /api/login/outLogin */
export async function outLogin(options?: { [key: string]: any }) {
  return request<Record<string, any>>('/api/login/outLogin', {
    method: 'POST',
    ...(options || {}),
  });
}

/** 获取当前用户信息 GET /api/currentUser */
export async function currentUser(options?: { [key: string]: any }) {
  return request<{
    data: API.CurrentUser;
  }>('/api/currentUser', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 获取流程模板列表 GET /api/process-templates */
export async function listProcessTemplates(
  params?: { name?: string; is_active?: boolean; deleted?: boolean },
  options?: { [key: string]: any },
) {
  return request<API.ProcessTemplate[]>('/api/process-templates', {
    method: 'GET',
    params: params,
    ...(options || {}),
  });
}

/** 创建流程模板 POST /api/process-templates */
export async function createProcessTemplate(
  body: API.CreateProcessTemplateRequest,
  options?: { [key: string]: any },
) {
  return request<API.ProcessTemplate>('/api/process-templates', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 复制流程模板 POST /api/process-templates/:id/copy */
export async function copyProcessTemplate(id: string, options?: { [key: string]: any }) {
  return request<API.ProcessTemplate>(`/api/process-templates/${id}/copy`, {
    method: 'POST',
    ...(options || {}),
  });
}

/** 更新流程模板 PATCH /api/process-templates/:id */
export async function updateProcessTemplate(
  id: string,
  body: { name?: string; description?: string; is_active?: boolean },
  options?: { [key: string]: any },
) {
  return request<API.ProcessTemplate>(`/api/process-templates/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 删除流程模板 DELETE /api/process-templates/:id */
export async function deleteProcessTemplate(id: string, options?: { [key: string]: any }) {
  return request<Record<string, any>>(`/api/process-templates/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 恢复流程模板 POST /api/process-templates/:id/restore */
export async function restoreProcessTemplate(id: string, options?: { [key: string]: any }) {
  return request<Record<string, any>>(`/api/process-templates/${id}/restore`, {
    method: 'POST',
    ...(options || {}),
  });
}

/** 彻底删除流程模板 DELETE /api/process-templates/:id/hard-delete */
export async function hardDeleteProcessTemplate(id: string, options?: { [key: string]: any }) {
  return request<Record<string, any>>(`/api/process-templates/${id}/hard-delete`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 获取节点模板列表 GET /api/process-templates/:id/node-templates */
export async function listNodeTemplates(
  processTemplateId: string,
  options?: { [key: string]: any },
) {
  return request<API.NodeTemplate[]>(`/api/process-templates/${processTemplateId}/node-templates`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建节点模板 POST /api/process-templates/:id/node-templates */
export async function createNodeTemplate(
  processTemplateId: string,
  body: API.CreateNodeTemplateRequest,
  options?: { [key: string]: any },
) {
  return request<API.NodeTemplate>(`/api/process-templates/${processTemplateId}/node-templates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 重排序节点模板 POST /api/process-templates/:templateId/node-templates/reorder */
export async function reorderNodeTemplates(
  templateId: string,
  nodeIds: string[],
  options?: { [key: string]: any },
) {
  return request<void>(`/api/process-templates/${templateId}/node-templates/reorder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { node_ids: nodeIds },
    ...(options || {}),
  });
}

/** 更新节点模板 PATCH /api/process-templates/:templateId/node-templates/:nodeId */
export async function updateNodeTemplate(
  templateId: string,
  nodeId: string,
  body: Partial<API.NodeTemplate>,
  options?: { [key: string]: any },
) {
  return request<API.NodeTemplate>(`/api/process-templates/${templateId}/node-templates/${nodeId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 删除节点模板 DELETE /api/process-templates/:templateId/node-templates/:nodeId */
export async function deleteNodeTemplate(
  templateId: string,
  nodeId: string,
  options?: { [key: string]: any },
) {
  return request<Record<string, any>>(
    `/api/process-templates/${templateId}/node-templates/${nodeId}`,
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );
}

/** 获取任务列表 GET /api/tasks */
export async function listTasks(
  params?: { status?: string; deleted?: boolean; [key: string]: any },
  options?: { [key: string]: any },
) {
  return request<API.Task[]>('/api/tasks', {
    method: 'GET',
    params: params,
    ...(options || {}),
  });
}

/** 获取单个任务 GET /api/tasks/:id */
export async function getTask(id: string, options?: { [key: string]: any }) {
  return request<API.Task>(`/api/tasks/${id}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建任务 POST /api/tasks */
export async function createTask(body: API.CreateTaskRequest, options?: { [key: string]: any }) {
  return request<API.Task>('/api/tasks', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 更新任务 PATCH /api/tasks/:id */
export async function updateTask(
  id: string,
  body: API.UpdateTaskRequest,
  options?: { [key: string]: any },
) {
  return request<API.Task>(`/api/tasks/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 删除任务 DELETE /api/tasks/:id */
export async function deleteTask(id: string, options?: { [key: string]: any }) {
  return request<void>(`/api/tasks/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 恢复任务 POST /api/tasks/:id/restore */
export async function restoreTask(id: string, options?: { [key: string]: any }) {
  return request<Record<string, any>>(`/api/tasks/${id}/restore`, {
    method: 'POST',
    ...(options || {}),
  });
}

/** 彻底删除任务 DELETE /api/tasks/:id/hard-delete */
export async function hardDeleteTask(id: string, options?: { [key: string]: any }) {
  return request<void>(`/api/tasks/${id}/hard-delete`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 复制任务 POST /api/tasks/:id/copy */
export async function copyTask(id: string, newTitle: string, options?: { [key: string]: any }) {
  return request<API.Task>(`/api/tasks/${id}/copy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { new_title: newTitle },
    ...(options || {}),
  });
}

/** 获取任务节点列表 GET /api/tasks/:id/nodes */
export async function listTaskNodes(taskId: string, options?: { [key: string]: any }) {
  return request<API.TaskNodeDetail[]>(`/api/tasks/${taskId}/nodes`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建任务节点 POST /api/tasks/:taskId/nodes */
export async function createTaskNode(
  taskId: string,
  body: { name: string; sequence: number; duration_days?: number; owner_dept_id?: string },
  options?: { [key: string]: any },
) {
  return request<API.TaskNode>(`/api/tasks/${taskId}/nodes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 删除任务节点 DELETE /api/tasks/:taskId/nodes/:nodeId */
export async function deleteTaskNode(
  taskId: string,
  nodeId: string,
  options?: { [key: string]: any },
) {
  return request<void>(`/api/tasks/${taskId}/nodes/${nodeId}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 重排序任务节点 POST /api/tasks/:taskId/nodes/reorder */
export async function reorderTaskNodes(
  taskId: string,
  nodeIds: string[],
  options?: { [key: string]: any },
) {
  return request<void>(`/api/tasks/${taskId}/nodes/reorder`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { node_ids: nodeIds },
    ...(options || {}),
  });
}

/** 更新任务节点 PATCH /api/tasks/:taskId/nodes/:nodeId */
export async function updateTaskNode(
  taskId: string,
  nodeId: string,
  body: API.UpdateTaskNodeRequest,
  options?: { [key: string]: any },
) {
  return request<API.TaskNode>(`/api/tasks/${taskId}/nodes/${nodeId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 指派任务节点负责人/部门 PATCH /api/tasks/:taskId/nodes/:nodeId/assign */
export async function assignTaskNodeOwner(
  taskId: string,
  nodeId: string,
  body: API.AssignTaskNodeOwnerRequest,
  options?: { [key: string]: any },
) {
  return request<API.TaskNode>(`/api/tasks/${taskId}/nodes/${nodeId}/assign`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 获取所有任务节点列表 GET /api/task-nodes */
export async function listAllTaskNodes(params: any, options?: { [key: string]: any }) {
  return request<API.TaskNodeDetail[]>('/api/task-nodes', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 获取我的任务节点列表 GET /api/task-nodes/my */
export async function listMyTaskNodes(params?: any, options?: { [key: string]: any }) {
  return request<API.TaskNodeDetail[]>('/api/task-nodes/my', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 获取部门列表 GET /api/departments */
export async function listDepartments(options?: { [key: string]: any }) {
  return request<API.Department[]>('/api/departments', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 更新部门 PATCH /api/departments/:id */
export async function updateDepartment(
  id: string,
  body: API.UpdateDepartmentRequest,
  options?: { [key: string]: any },
) {
  return request<API.Department>(`/api/departments/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 创建部门 POST /api/departments */
export async function createDepartment(
  body: API.CreateDepartmentRequest,
  options?: { [key: string]: any },
) {
  return request<API.Department>('/api/departments', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 删除部门 DELETE /api/departments/:id */
export async function deleteDepartment(id: string, options?: { [key: string]: any }) {
  return request<{ success: boolean }>(`/api/departments/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 更新用户 PATCH /api/users/:id */
export async function updateUser(
  id: string,
  body: API.UpdateUserRequest,
  options?: { [key: string]: any },
) {
  return request<API.User>(`/api/users/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 获取角色列表 GET /api/roles */
export async function listRoles(options?: { [key: string]: any }) {
  return request<API.Role[]>('/api/roles', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建角色 POST /api/roles */
export async function createRole(body: API.CreateRoleRequest, options?: { [key: string]: any }) {
  return request<API.Role>('/api/roles', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 更新角色 PATCH /api/roles/:id */
export async function updateRole(
  id: string,
  body: { name?: string },
  options?: { [key: string]: any },
) {
  return request<API.Role>(`/api/roles/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 删除角色 DELETE /api/roles/:id */
export async function deleteRole(id: string, options?: { [key: string]: any }) {
  return request<{ success: boolean }>(`/api/roles/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 获取用户列表 GET /api/users */
export async function listUsers(options?: { [key: string]: any }) {
  return request<API.User[]>('/api/users', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建用户 POST /api/users */
export async function createUser(body: API.CreateUserRequest, options?: { [key: string]: any }) {
  return request<API.User>('/api/users', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 添加节点参与人 POST /api/tasks/:taskId/nodes/:nodeId/participants */
export async function addNodeParticipant(
  taskId: string,
  nodeId: string,
  body: { user_id?: string; dept_id?: string; role?: string },
  options?: { [key: string]: any },
) {
  return request<API.TaskNodeParticipant>(`/api/tasks/${taskId}/nodes/${nodeId}/participants`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 移除节点参与人 DELETE /api/tasks/:taskId/nodes/:nodeId/participants/:participantId */
export async function removeNodeParticipant(
  taskId: string,
  nodeId: string,
  participantId: string,
  options?: { [key: string]: any },
) {
  return request<any>(`/api/tasks/${taskId}/nodes/${nodeId}/participants/${participantId}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}
export async function listPermissions(options?: { [key: string]: any }) {
  return request<API.PermissionItem[]>('/api/permissions', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 获取角色的权限 GET /api/roles/:code/permissions */
export async function getRolePermissions(code: string, options?: { [key: string]: any }) {
  return request<API.RolePermissions>(`/api/roles/${code}/permissions`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 更新角色的权限 POST /api/roles/:code/permissions */
export async function updateRolePermissions(
  code: string,
  body: { permissions: string[] },
  options?: { [key: string]: any },
) {
  return request<Record<string, any>>(`/api/roles/${code}/permissions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

